import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './ShopsSortFilter.styles';
import { sortOptions } from './ShopsFilters.helpers';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { useResponsive } from '@/hooks/useResponsive';
import { SelectOption } from '@/types';

export interface ShopsSortFilterProps {
  sortValue?: string;
  onSortChange?: (value: string) => void;
}

export const ShopsSortFilter: React.FC<ShopsSortFilterProps> = ({
  sortValue = 'default',
  onSortChange,
}) => {
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();

  const sortOptionsMemo = useMemo(() => sortOptions(t), [t]);
  const [isBSOpen, toggleBS] = useToggleOpenState();
  const selectedSort =
    sortOptionsMemo.find(o => o.value === sortValue) ?? sortOptionsMemo[0];

  const sortDropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutsideSort = useCallback(
    (e: MouseEvent) => {
      const target = e.target as Node;
      const el = sortDropdownRef.current;
      const parent = el?.parentElement;
      if (el && parent && !parent.contains(target) && !el.contains(target)) {
        toggleBS(false);
      }
    },
    [toggleBS]
  );

  useEffect(() => {
    if (isBSOpen && isDesktop) {
      document.addEventListener('mousedown', handleClickOutsideSort);
      return () =>
        document.removeEventListener('mousedown', handleClickOutsideSort);
    }
  }, [isBSOpen, isDesktop, handleClickOutsideSort]);

  const handleSortChange = (o: SelectOption) => {
    onSortChange?.(o.value);
    toggleBS(false);
  };

  return (
    <>
      <S.SortControlWrap $isActive={isBSOpen} ref={sortDropdownRef}>
        <S.Control
          size="s"
          appearance="defaultOutlined"
          onClick={toggleBS}
          data-stat="shops-sort-filter"
          $sortOpen={isBSOpen}
        >
          {isDesktop ? selectedSort.label : t('pages.shops.sort')}
        </S.Control>

        {isDesktop && isBSOpen && (
          <S.SortDropdownPanel
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
          >
            <S.SortMenu>
              {sortOptionsMemo.map(o => (
                <S.SortMenuItem
                  key={o.value}
                  type="button"
                  $isActive={o.value === selectedSort.value}
                  onClick={() => handleSortChange(o)}
                  data-stat={o.dataStat}
                >
                  {o.label}
                  <S.SortRadio $isActive={o.value === selectedSort.value} />
                </S.SortMenuItem>
              ))}
            </S.SortMenu>
          </S.SortDropdownPanel>
        )}
      </S.SortControlWrap>

      {!isDesktop && (
        <BottomSheet
          isOpen={isBSOpen}
          close={() => toggleBS(false)}
          title={t('pages.shops.sort')}
          isInPortal
        >
          {sortOptionsMemo.map(o => (
            <S.SortOptionButtonMobile
              key={o.value}
              type="button"
              onClick={() => handleSortChange(o)}
              data-stat={o.dataStat}
            >
              {o.label}
              <S.SortOptionRadioMobile
                $active={o.value === selectedSort.value}
              />
            </S.SortOptionButtonMobile>
          ))}
        </BottomSheet>
      )}
    </>
  );
};
